/**
 * „Plattform → Verbindungen" (ADR 0017).
 *
 * Der Ort, an dem ein Zugang **einmal** eingetragen wird: Versand-Provider,
 * IMAP-Postfach, KI-Anbieter, SkillLock. Alles Uebrige im System zeigt nur
 * noch darauf.
 *
 * Die Seite gehoert **keinem Mandanten** — sie ist nur fuer Plattform-Admins
 * sichtbar, und die API prueft das noch einmal (`requirePlatformAdmin`). Die
 * Sichtbarkeit hier ist Bequemlichkeit, nicht Schutz.
 *
 * **Geheimnisse sind write-only**: die API liefert nur `has_api_key`,
 * `has_secret_key`, `has_password`. Ein leeres Feld heisst „unverändert
 * lassen", nicht „löschen".
 *
 * Drei Dinge, die man beim Einrichten braucht, stehen deshalb je Verbindung
 * zusammen: die **Freigaben** (welcher Mandant darf sie wählen), der
 * **Test-Knopf** (hat der Zugang je funktioniert?) und die **Nutzung** (was
 * bräche, wenn sie verschwände — und warum das Löschen scheitert).
 */
import {
  AI_PROVIDERS,
  CONNECTION_TYPES,
  OUTBOUND_MAIL_PROVIDERS,
  SCALEWAY_REGION_DEFAULT,
  SCALEWAY_REGIONS,
  DEFAULT_AI_EMBEDDING_MODELS,
  DEFAULT_AI_MODELS,
  IMAP_PORT_DEFAULT,
  type AiProviderName,
  type Connection,
  type ConnectionConfigInput,
  type ConnectionDetail,
  type ConnectionType,
  type OutboundMailProvider,
  type PlatformTenant,
  type ScalewayRegion,
} from '@tickethub/shared';
import { PlugZap, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { toastError } from '@/lib/errors';
import { useSession } from '@/lib/session';
import { platformApi } from '@/lib/tickets';

/**
 * Ein Formular fuer alle vier Typen. Bewusst **ein** flaches Objekt statt
 * einer Union: der Betreiber schaltet beim Anlegen zwischen den Typen hin und
 * her, und ein Formular, das dabei seine Eingaben vergisst, ist aergerlich.
 * Was wirklich geschickt wird, entscheidet `toConfig()` am Typ.
 */
type Form = {
  name: string;
  type: ConnectionType;
  // mail_provider
  mailProvider: OutboundMailProvider;
  apiKey: string;
  secretKey: string;
  accessKey: string;
  projectId: string;
  region: ScalewayRegion;
  domains: string;
  // imap_account
  host: string;
  port: string;
  user: string;
  password: string;
  secure: boolean;
  // ai_provider
  aiProvider: AiProviderName;
  model: string;
  embeddingModel: string;
  embeddingApiKey: string;
  // skilllock
  baseUrl: string;
  defaultBoardId: string;
  webhookSecret: string;
};

const EMPTY: Form = {
  name: '',
  type: 'mail_provider',
  mailProvider: 'scaleway',
  apiKey: '',
  secretKey: '',
  accessKey: '',
  projectId: '',
  region: SCALEWAY_REGION_DEFAULT,
  domains: '',
  host: '',
  port: String(IMAP_PORT_DEFAULT),
  user: '',
  password: '',
  secure: true,
  aiProvider: 'anthropic',
  model: DEFAULT_AI_MODELS.anthropic,
  embeddingModel: DEFAULT_AI_EMBEDDING_MODELS.anthropic,
  embeddingApiKey: '',
  baseUrl: '',
  defaultBoardId: '',
  webhookSecret: '',
};

function formOf(connection: Connection): Form {
  const base: Form = { ...EMPTY, name: connection.name, type: connection.type };
  const config = connection.config;
  switch (config.type) {
    case 'mail_provider':
      return {
        ...base,
        mailProvider: config.provider,
        accessKey: config.access_key ?? '',
        projectId: config.project_id ?? '',
        region: config.region ?? SCALEWAY_REGION_DEFAULT,
        domains: config.domains.join('\n'),
      };
    case 'imap_account':
      return {
        ...base,
        host: config.host,
        port: String(config.port),
        user: config.user,
        secure: config.secure,
      };
    case 'ai_provider':
      return {
        ...base,
        aiProvider: config.provider,
        model: config.model,
        embeddingModel: config.embedding_model,
      };
    case 'skilllock':
      return {
        ...base,
        baseUrl: config.base_url,
        defaultBoardId: config.default_board_id ?? '',
      };
  }
}

/** Formular → API-Eingabe. Ein leeres Geheimnis wird nicht mitgeschickt. */
function toConfig(form: Form): ConnectionConfigInput {
  switch (form.type) {
    case 'mail_provider': {
      const scaleway = form.mailProvider === 'scaleway';
      return {
        type: 'mail_provider',
        provider: form.mailProvider,
        access_key: scaleway && form.accessKey.trim().length > 0 ? form.accessKey.trim() : null,
        project_id: scaleway && form.projectId.trim().length > 0 ? form.projectId.trim() : null,
        region: scaleway ? form.region : null,
        domains: form.domains
          .split(/[\n,;\s]+/)
          .map((entry) => entry.trim().toLowerCase())
          .filter((entry) => entry.length > 0),
        ...(!scaleway && form.apiKey.length > 0 ? { api_key: form.apiKey } : {}),
        ...(scaleway && form.secretKey.length > 0 ? { secret_key: form.secretKey } : {}),
      };
    }
    case 'imap_account':
      return {
        type: 'imap_account',
        host: form.host.trim(),
        port: Number(form.port),
        user: form.user.trim(),
        secure: form.secure,
        ...(form.password.length > 0 ? { password: form.password } : {}),
      };
    case 'ai_provider':
      return {
        type: 'ai_provider',
        provider: form.aiProvider,
        model: form.model.trim(),
        embedding_model: form.embeddingModel.trim(),
        ...(form.apiKey.length > 0 ? { api_key: form.apiKey } : {}),
        ...(form.embeddingApiKey.length > 0 ? { embedding_api_key: form.embeddingApiKey } : {}),
      };
    case 'skilllock':
      return {
        type: 'skilllock',
        base_url: form.baseUrl.trim(),
        default_board_id: form.defaultBoardId.trim().length > 0 ? form.defaultBoardId.trim() : null,
        ...(form.apiKey.length > 0 ? { api_key: form.apiKey } : {}),
        ...(form.webhookSecret.length > 0 ? { webhook_secret: form.webhookSecret } : {}),
      };
  }
}

/** „Ist ein Geheimnis hinterlegt?" — je Typ ein anderes Feld. */
function hasSecret(connection: Connection | null): boolean {
  const config = connection?.config;
  if (!config) return false;
  switch (config.type) {
    case 'mail_provider':
      return config.has_api_key || config.has_secret_key;
    case 'imap_account':
      return config.has_password;
    case 'ai_provider':
      return config.has_api_key;
    case 'skilllock':
      return config.has_api_key;
  }
}

export function PlatformConnectionsPage() {
  const { t } = useTranslation();
  const { user } = useSession();

  const [items, setItems] = useState<Connection[] | null>(null);
  const [tenants, setTenants] = useState<PlatformTenant[]>([]);
  const [detail, setDetail] = useState<ConnectionDetail | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [form, setForm] = useState<Form>(EMPTY);
  const [grants, setGrants] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const selected = items?.find((entry) => entry.id === selectedId) ?? null;

  const load = useCallback(async () => {
    try {
      const [connections, tenantList] = await Promise.all([
        platformApi.connections(),
        platformApi.tenants(),
      ]);
      setItems(connections.items);
      setTenants(tenantList.items);
    } catch (error) {
      setItems([]);
      toastError(error, t);
    }
  }, [t]);

  useEffect(() => {
    if (!user?.is_platform_admin) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Daten beim Mount laden
    void load();
  }, [load, user?.is_platform_admin]);

  function startCreate() {
    setSelectedId(null);
    setDetail(null);
    setForm(EMPTY);
    setGrants([]);
  }

  async function startEdit(connection: Connection) {
    setSelectedId(connection.id);
    setForm(formOf(connection));
    setGrants(connection.granted_tenant_ids);
    try {
      setDetail(await platformApi.connection(connection.id));
    } catch (error) {
      toastError(error, t);
    }
  }

  async function save() {
    setBusy(true);
    try {
      const config = toConfig(form);
      if (selectedId === null) {
        const created = await platformApi.createConnection({
          name: form.name.trim(),
          config,
          tenant_ids: grants,
        });
        toast.success(t('connections.saved'));
        await load();
        await startEdit(created);
      } else {
        await platformApi.saveConnection(selectedId, {
          name: form.name.trim(),
          config,
          tenant_ids: grants,
        });
        toast.success(t('connections.saved'));
        await load();
        setDetail(await platformApi.connection(selectedId));
        // Geheimnisse nach dem Speichern aus dem Formular nehmen.
        setForm((current) => ({
          ...current,
          apiKey: '',
          secretKey: '',
          password: '',
          embeddingApiKey: '',
          webhookSecret: '',
        }));
      }
    } catch (error) {
      toastError(error, t);
    } finally {
      setBusy(false);
    }
  }

  async function runTest(connectionId: string) {
    setBusy(true);
    try {
      const result = await platformApi.testConnection(connectionId);
      toast.success(t('connections.test_ok', { detail: result.detail }));
    } catch (error) {
      toastError(error, t);
    } finally {
      setBusy(false);
    }
  }

  async function remove(connectionId: string) {
    setBusy(true);
    try {
      await platformApi.removeConnection(connectionId);
      toast.success(t('connections.deleted'));
      startCreate();
      await load();
    } catch (error) {
      toastError(error, t);
    } finally {
      setBusy(false);
    }
  }

  if (!user?.is_platform_admin) {
    return (
      <Alert>
        <AlertDescription>{t('platform.forbidden')}</AlertDescription>
      </Alert>
    );
  }

  const mail = form.type === 'mail_provider';
  const scaleway = mail && form.mailProvider === 'scaleway';

  return (
    <div className="space-y-8">
      <section className="space-y-2">
        <h1 className="text-xl font-semibold">{t('connections.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('connections.intro')}</p>
      </section>

      <section className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-lg font-semibold">{t('connections.list_title')}</h2>
          <Button size="sm" variant="outline" className="ml-auto" onClick={() => startCreate()}>
            {t('connections.new')}
          </Button>
        </div>

        {items === null ? (
          <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
        ) : items.length === 0 ? (
          <Alert>
            <AlertDescription>{t('connections.empty')}</AlertDescription>
          </Alert>
        ) : (
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left">
                <tr>
                  <th className="px-3 py-2 font-medium">{t('connections.name')}</th>
                  <th className="px-3 py-2 font-medium">{t('connections.type')}</th>
                  <th className="px-3 py-2 font-medium">{t('connections.provider')}</th>
                  <th className="px-3 py-2 font-medium">{t('connections.grants')}</th>
                  <th className="px-3 py-2 font-medium">{t('connections.usage_count')}</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {items.map((connection) => (
                  <tr key={connection.id} className="border-t">
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        className="text-left font-medium underline-offset-2 hover:underline"
                        onClick={() => void startEdit(connection)}
                      >
                        {connection.name}
                      </button>
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant="outline">{t(`connections.type_${connection.type}`)}</Badge>
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{connection.provider}</td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {connection.granted_tenant_ids.length}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{connection.usage_count}</td>
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={busy}
                          onClick={() => void runTest(connection.id)}
                        >
                          <PlugZap aria-hidden="true" />
                          {t('connections.test')}
                        </Button>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          aria-label={t('connections.delete', { name: connection.name })}
                          disabled={busy}
                          onClick={() => void remove(connection.id)}
                        >
                          <Trash2 aria-hidden="true" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="space-y-4 rounded-lg border p-4">
        <h2 className="text-lg font-semibold">
          {selectedId === null ? t('connections.new') : t('connections.edit')}
        </h2>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="conn-name">{t('connections.name')}</Label>
            <Input
              id="conn-name"
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="conn-type">{t('connections.type')}</Label>
            <Select
              id="conn-type"
              value={form.type}
              disabled={selectedId !== null}
              onChange={(event) =>
                setForm({ ...form, type: event.target.value as ConnectionType })
              }
            >
              {CONNECTION_TYPES.map((type) => (
                <option key={type} value={type}>
                  {t(`connections.type_${type}`)}
                </option>
              ))}
            </Select>
            {selectedId !== null ? (
              <p className="text-xs text-muted-foreground">{t('connections.type_locked')}</p>
            ) : null}
          </div>

          {mail ? (
            <>
              <div className="space-y-2">
                <Label htmlFor="conn-mail-provider">{t('mail.provider')}</Label>
                <Select
                  id="conn-mail-provider"
                  value={form.mailProvider}
                  onChange={(event) =>
                    setForm({ ...form, mailProvider: event.target.value as OutboundMailProvider })
                  }
                >
                  {OUTBOUND_MAIL_PROVIDERS.map((provider) => (
                    <option key={provider} value={provider}>
                      {t(`mail.provider_${provider}`)}
                    </option>
                  ))}
                </Select>
              </div>

              {scaleway ? (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="conn-secret">{t('mail.secret_key')}</Label>
                    <Input
                      id="conn-secret"
                      type="password"
                      autoComplete="new-password"
                      placeholder={hasSecret(selected) ? t('mail.secret_set') : ''}
                      value={form.secretKey}
                      onChange={(event) => setForm({ ...form, secretKey: event.target.value })}
                    />
                    <p className="text-xs text-muted-foreground">{t('mail.scaleway_key_hint')}</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="conn-access-key">{t('mail.access_key')}</Label>
                    <Input
                      id="conn-access-key"
                      value={form.accessKey}
                      placeholder="SCWXXXXXXXXXXXXXXXXX"
                      onChange={(event) => setForm({ ...form, accessKey: event.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="conn-project">{t('mail.project_id')}</Label>
                    <Input
                      id="conn-project"
                      value={form.projectId}
                      onChange={(event) => setForm({ ...form, projectId: event.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="conn-region">{t('mail.region')}</Label>
                    <Select
                      id="conn-region"
                      value={form.region}
                      onChange={(event) =>
                        setForm({ ...form, region: event.target.value as ScalewayRegion })
                      }
                    >
                      {SCALEWAY_REGIONS.map((region) => (
                        <option key={region} value={region}>
                          {region}
                        </option>
                      ))}
                    </Select>
                  </div>
                </>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="conn-api-key">{t('mail.api_key')}</Label>
                  <Input
                    id="conn-api-key"
                    type="password"
                    autoComplete="new-password"
                    placeholder={hasSecret(selected) ? t('mail.secret_set') : ''}
                    value={form.apiKey}
                    onChange={(event) => setForm({ ...form, apiKey: event.target.value })}
                  />
                  <p className="text-xs text-muted-foreground">{t('mail.secret_hint')}</p>
                </div>
              )}

              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="conn-domains">{t('connections.domains')}</Label>
                <textarea
                  id="conn-domains"
                  className="min-h-20 w-full rounded-md border border-input bg-background p-2 text-sm"
                  value={form.domains}
                  onChange={(event) => setForm({ ...form, domains: event.target.value })}
                />
                <p className="text-xs text-muted-foreground">{t('connections.domains_hint')}</p>
              </div>
            </>
          ) : null}

          {form.type === 'imap_account' ? (
            <>
              <div className="space-y-2">
                <Label htmlFor="conn-host">{t('mail.imap_host')}</Label>
                <Input
                  id="conn-host"
                  value={form.host}
                  onChange={(event) => setForm({ ...form, host: event.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="conn-port">{t('mail.imap_port')}</Label>
                <Input
                  id="conn-port"
                  inputMode="numeric"
                  value={form.port}
                  onChange={(event) => setForm({ ...form, port: event.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="conn-user">{t('mail.imap_user')}</Label>
                <Input
                  id="conn-user"
                  autoComplete="off"
                  value={form.user}
                  onChange={(event) => setForm({ ...form, user: event.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="conn-password">{t('mail.imap_password')}</Label>
                <Input
                  id="conn-password"
                  type="password"
                  autoComplete="new-password"
                  placeholder={hasSecret(selected) ? t('mail.secret_set') : ''}
                  value={form.password}
                  onChange={(event) => setForm({ ...form, password: event.target.value })}
                />
                <p className="text-xs text-muted-foreground">{t('mail.secret_hint')}</p>
              </div>
              <label className="flex items-center gap-2 self-end text-sm">
                <input
                  type="checkbox"
                  checked={form.secure}
                  onChange={(event) => setForm({ ...form, secure: event.target.checked })}
                />
                {t('mail.imap_secure')}
              </label>
            </>
          ) : null}

          {form.type === 'ai_provider' ? (
            <>
              <div className="space-y-2">
                <Label htmlFor="conn-ai-provider">{t('ai.settings_provider')}</Label>
                <Select
                  id="conn-ai-provider"
                  value={form.aiProvider}
                  onChange={(event) => {
                    const provider = event.target.value as AiProviderName;
                    setForm({
                      ...form,
                      aiProvider: provider,
                      model: DEFAULT_AI_MODELS[provider],
                      embeddingModel: DEFAULT_AI_EMBEDDING_MODELS[provider],
                    });
                  }}
                >
                  {AI_PROVIDERS.map((provider) => (
                    <option key={provider} value={provider}>
                      {t(`ai.provider_${provider}`)}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="conn-model">{t('ai.settings_model')}</Label>
                <Input
                  id="conn-model"
                  value={form.model}
                  onChange={(event) => setForm({ ...form, model: event.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="conn-ai-key">{t('ai.settings_api_key')}</Label>
                <Input
                  id="conn-ai-key"
                  type="password"
                  autoComplete="off"
                  placeholder={hasSecret(selected) ? t('mail.secret_set') : ''}
                  value={form.apiKey}
                  onChange={(event) => setForm({ ...form, apiKey: event.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="conn-embedding-model">{t('ai.settings_embedding_model')}</Label>
                <Input
                  id="conn-embedding-model"
                  value={form.embeddingModel}
                  onChange={(event) => setForm({ ...form, embeddingModel: event.target.value })}
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="conn-embedding-key">{t('ai.settings_embedding_key')}</Label>
                <Input
                  id="conn-embedding-key"
                  type="password"
                  autoComplete="off"
                  value={form.embeddingApiKey}
                  onChange={(event) => setForm({ ...form, embeddingApiKey: event.target.value })}
                />
                <p className="text-xs text-muted-foreground">{t('ai.settings_embedding_hint')}</p>
              </div>
            </>
          ) : null}

          {form.type === 'skilllock' ? (
            <>
              <div className="space-y-2">
                <Label htmlFor="conn-base-url">{t('skilllock.base_url')}</Label>
                <Input
                  id="conn-base-url"
                  type="url"
                  value={form.baseUrl}
                  placeholder="https://skilllock.example/api"
                  onChange={(event) => setForm({ ...form, baseUrl: event.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="conn-board">{t('skilllock.default_board')}</Label>
                <Input
                  id="conn-board"
                  value={form.defaultBoardId}
                  onChange={(event) => setForm({ ...form, defaultBoardId: event.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="conn-sl-key">{t('skilllock.api_key')}</Label>
                <Input
                  id="conn-sl-key"
                  type="password"
                  autoComplete="off"
                  placeholder={hasSecret(selected) ? t('common.secret_set') : ''}
                  value={form.apiKey}
                  onChange={(event) => setForm({ ...form, apiKey: event.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="conn-sl-secret">{t('skilllock.webhook_secret')}</Label>
                <Input
                  id="conn-sl-secret"
                  type="password"
                  autoComplete="off"
                  value={form.webhookSecret}
                  onChange={(event) => setForm({ ...form, webhookSecret: event.target.value })}
                />
                <p className="text-xs text-muted-foreground">{t('skilllock.webhook_help')}</p>
              </div>
            </>
          ) : null}
        </div>

        {/* ── Freigaben: ohne sie sieht kein Mandant die Verbindung ───────── */}
        <div className="space-y-2">
          <p className="text-sm font-medium">{t('connections.grants')}</p>
          <p className="text-xs text-muted-foreground">{t('connections.grants_hint')}</p>
          <ul className="grid gap-1 sm:grid-cols-2">
            {tenants.map((tenant) => (
              <li key={tenant.id}>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={grants.includes(tenant.id)}
                    onChange={(event) =>
                      setGrants((current) =>
                        event.target.checked
                          ? [...current, tenant.id]
                          : current.filter((id) => id !== tenant.id),
                      )
                    }
                  />
                  {tenant.name}
                </label>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button disabled={busy} onClick={() => void save()}>
            {t('connections.save')}
          </Button>
          {selectedId !== null ? (
            <Button variant="outline" disabled={busy} onClick={() => void runTest(selectedId)}>
              <PlugZap aria-hidden="true" />
              {t('connections.test')}
            </Button>
          ) : null}
        </div>

        {detail && detail.usage.length > 0 ? (
          <div className="space-y-1">
            <p className="text-sm font-medium">{t('connections.usage_title')}</p>
            <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
              {detail.usage.map((entry) => (
                <li key={`${entry.kind}:${entry.label}`}>{entry.label}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </section>
    </div>
  );
}
